import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AptosNormal from "../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../utils/Customfonts/Aptos-Bold.ttf";
import AptosItalic from "../../../utils/Customfonts/Aptos-Italic.ttf";
import moment from "moment";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Aptos",
  normalFont: "Aptos",
  italic: "italic",
  italicFont: "Aptos",
};

// DYNAMIC CONFIGURATION 
export const config = {
  margin: 30,
  borderMargin: 15,
  headerHeight: 50,
  headerLogo: "/img/dycgpl-logo.webp",
  footerHeight: 25,

  fontSize: 9.5,
  fontSizeSm: 8.5,
  fontSizeLg: 11.5,
  fontSizeTitle: 13.5,

  cellPadding: 5.5,
  sectionGap: 24,
  subSectionGap: 18,
  lineGap: 25,

  get contentTop() {
    return this.borderMargin + 58;
  },
  get contentBottom() {
    return this.borderMargin + 25;
  },
};

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.4,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: [240, 240, 240],
  valign: "middle",
});

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },

  afterTable(gap) {
    this.y = this.doc.lastAutoTable.finalY + (gap ?? config.sectionGap);
  },

  advance(delta) {
    this.y += delta;
  },

  ensureSpace(needed = 25) {
    if (this.y + needed > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },
};

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const fontResponse = await fetch(fontUrl);
    const fontBuffer = await fontResponse.arrayBuffer();
    const fontBase64 = arrayBufferToBase64(fontBuffer);
    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, fontBase64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (err) {
    console.error("Font load error:", fontName, err);
  }
};

const addPageDecorators = (doc, totalPages, empData = {}) => {
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    const pWidth = layout.pageWidth;
    const pHeight = layout.pageHeight;
    const bm = config.borderMargin;

    // Outer Rectangular Page Border Box
    doc.setDrawColor(0);
    doc.setLineWidth(0.8);
    doc.rect(bm, bm, pWidth - bm * 2, pHeight - bm * 2);

    // Header Left Text (Official Application Form Header)
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text(empData.companyTitle || "DYC GLOBAL PVT.LTD.", bm + 10, bm + 16);

    doc.setFontSize(11.5);
    doc.text(empData.formTitle || "APPLICATION FORM", bm + 10, bm + 28);

    const formRefText = empData.formRefText || empData.fmtCode || "DYC/FMT - HR/02, Rev.00, Dated:24.05.2023";
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(8.5);
    doc.text(formRefText, bm + 10, bm + 39);

    // DYC Header Logo (Top Right)
    try {
      const logoH = 38;
      const logoW = 38;
      const logoY = bm + 3;
      const logoX = pWidth - bm - logoW - 6;
      const logoPath = config.headerLogo || DYC_LOGO;

      try {
        doc.addImage(logoPath, "WEBP", logoX, logoY, logoW, logoH);
      } catch (_) {
        try {
          doc.addImage(logoPath, "PNG", logoX, logoY, logoW, logoH);
        } catch (__) {
          doc.addImage(logoPath, logoX, logoY, logoW, logoH);
        }
      }
    } catch (_) { }

    // Header Horizontal Line Divider
    doc.setLineWidth(0.6);
    doc.line(bm, bm + 46, pWidth - bm, bm + 46);

    // Footer Left & Right 
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(8.5);
    doc.setTextColor(0);
    doc.text("Company General Use. All Rights Reserved By DYC Global Pvt. Ltd.", bm + 10, pHeight - bm - 8);

    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.text(`Page ${page} of ${totalPages}`, pWidth - bm - 60, pHeight - bm - 8);
  }
};

const drawCheckbox = (doc, x, y, isChecked = false, label = "", fSize = 9) => {
  doc.setDrawColor(0, 0, 0);
  doc.setTextColor(0, 0, 0);
  doc.setLineWidth(0.6);
  doc.rect(x, y - 6, 7, 7);
  if (isChecked) {
    doc.setLineWidth(1.2);
    doc.line(x + 1.5, y - 3, x + 3.2, y - 1);
    doc.line(x + 3.2, y - 1, x + 6, y - 5);
    doc.setLineWidth(0.6);
  }
  if (label) {
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(fSize);
    doc.text(label, x + 10, y);
  }
};

const formatDateStr = (val) => {
  if (!val) return "";
  const m = moment(val, ["YYYY-MM-DD", "DD/MM/YYYY", "YYYY/MM/DD", moment.ISO_8601]);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(val);
};

export const generateApplicationFormPDF = async (empData = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  // Guard against jsPDF throwing when passed non-string (numbers, null, undefined)
  const _origText = doc.text.bind(doc);
  doc.text = function (text, x, y, options, transform) {
    let safe = text;
    if (safe === null || safe === undefined) safe = "";
    else if (typeof safe === "number") safe = String(safe);
    else if (Array.isArray(safe)) safe = safe.map((t) => (t === null || t === undefined ? "" : String(t)));
    return _origText(safe, x, y, options, transform);
  };

  try {
    await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos", customFontFamily.normalFont, customFontFamily.normal),
      loadAndAddFont(doc, AptosBold, "Aptos-Bold", customFontFamily.boldFont, customFontFamily.bold),
      loadAndAddFont(doc, AptosItalic, "Aptos-Italic", customFontFamily.italicFont, customFontFamily.italic),
      loadAndAddFont(doc, AptosBold, "Aptos-BoldItalic", customFontFamily.normalFont, "bolditalic"),
    ]);
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  } catch (fontErr) {
    console.warn("Could not load Aptos font, falling back to standard font:", fontErr);
    try {
      doc.setFont("helvetica", "normal");
    } catch (_) {}
  }

  layout.init(doc);
  const startX = config.margin;
  const contentW = layout.contentWidth;

  // -------------------------------------------------------------
  // PAGE 1 - APPLICANT SECTION, PERSONAL DETAILS & FAMILY BACKGROUND
  // -------------------------------------------------------------
  layout.y = config.contentTop + 10;

  // Title: APPLICANT SECTION
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(13);
  doc.text("APPLICANT SECTION", layout.pageWidth / 2, layout.y, { align: "center" });

  // Passport Photo Box (Top Right)
  const photoBoxW = 80;
  const photoBoxH = 95;
  const photoBoxX = layout.pageWidth - config.margin - photoBoxW - 5;
  const photoBoxY = layout.y - 5;

  doc.rect(photoBoxX, photoBoxY, photoBoxW, photoBoxH);

  layout.advance(45);

  // Position applied for & Location
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text("Position applied for: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.hrDesignation || empData.designation || "", startX + 95, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Location: ", startX + 245, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.location || empData.state || "", startX + 290, layout.y);
  layout.advance(26);

  // Reference from
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Reference from: - Advertisement/Friend/Relative/Campus/other", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.referenceFrom || "", startX + 295, layout.y);
  layout.advance(36);

  // Section Header: Personal details
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11.5);
  doc.text("Personal details", startX, layout.y);
  layout.advance(18);

  const drawLineField = (label, val, yPos, labelWidth = 100) => {
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(9.5);
    doc.text(label, startX, yPos);
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.text(String(val || ""), startX + labelWidth, yPos);
  };

  drawLineField("Name:", empData.name || "", layout.y, 45);
  layout.advance(17.5);
  drawLineField("Current Address:", empData.currentAddress || "", layout.y, 90);
  layout.advance(17.5);
  drawLineField("Permanent Address:", empData.permenantAddress || "", layout.y, 105);
  layout.advance(17.5);

  // Contact info fields
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text("Mobile: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.contactNumber || "", startX + 45, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Emergency Contact Number: ", startX + 240, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.emergencyContactNumber || "", startX + 375, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Telephone: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.telephone || "-", startX + 60, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Name and Relation (Emergency Contact): ", startX + 240, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.contactPersonName || "-", startX + 430, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Email: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.emailAddress || "", startX + 40, layout.y);
  layout.advance(17.5);

  // DOB, Age, Sex, Nationality
  const ageStr = empData.dateOfBirth ? `${moment().diff(moment(empData.dateOfBirth), "years")} yr` : "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Date of Birth: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(formatDateStr(empData.dateOfBirth), startX + 68, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Age: ", startX + 145, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(ageStr, startX + 170, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Sex: ", startX + 225, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.gender || "", startX + 250, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Nationality: ", startX + 310, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.nationality || "Indian", startX + 365, layout.y);
  layout.advance(30);

  const getYesNoDetails = (val) => {
    if (!val) return { isYes: false, isNo: true, text: "" };
    const str = String(val).trim();
    const lower = str.toLowerCase();
    if (lower === "no" || lower === "none" || lower === "nil" || lower === "false" || lower === "0") {
      return { isYes: false, isNo: true, text: "" };
    }
    if (lower === "yes" || lower === "true" || lower === "1") {
      return { isYes: true, isNo: false, text: "" };
    }
    if (lower.startsWith("yes")) {
      return { isYes: true, isNo: false, text: str };
    }
    return { isYes: false, isNo: true, text: str };
  };

  const phys = getYesNoDetails(empData.physicalDisable);
  const restr = getYesNoDetails(empData.restriction);
  const conv = getYesNoDetails(empData.convicted);

  // Physical disability
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Physical disability: ", startX, layout.y);
  drawCheckbox(doc, startX + 90, layout.y, phys.isYes, "Yes");
  drawCheckbox(doc, startX + 135, layout.y, phys.isNo, `No / if any pls Specify: ${phys.text}`);
  layout.advance(17.5);

  // Restrictions
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Restrictions: ", startX, layout.y);
  drawCheckbox(doc, startX + 65, layout.y, restr.isYes, "Yes");
  drawCheckbox(doc, startX + 110, layout.y, restr.isNo, `No / if any on activities for health reasons: ${restr.text}`);
  layout.advance(17.5);

  // Specify Major illness or operations, if any
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Specify Major illness or operations, if any: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.majorIlnes || empData.majorIllness || "NO", startX + 205, layout.y);
  layout.advance(17.5);

  // Have you ever been convicted ?
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Have you ever been convicted ? (Ignore minor traffic offences): ", startX, layout.y);
  drawCheckbox(doc, startX + 285, layout.y, conv.isYes, "Yes");
  drawCheckbox(doc, startX + 325, layout.y, conv.isNo, `No / If Yes pls specify: ${conv.text}`);
  layout.advance(17.5);

  // Marital Status
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Marital Status: Single/Married/Any other (Please specify):", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(`${empData.maritalStatus || ""}`, startX + 250, layout.y);
  layout.advance(30);

  // Section Header: Family Background:
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11.5);
  doc.text("Family Background:", startX, layout.y);
  layout.advance(12);

  const familyList = Array.isArray(empData.familyMembers) && empData.familyMembers.length > 0
    ? empData.familyMembers
    : (Array.isArray(empData.familyData) && empData.familyData.length > 0 ? empData.familyData : []);

  let familyRows = [];

  if (familyList.length > 0) {
    familyRows = familyList
      .filter((f) => f && (f.nomineeName || f.name || f.age || f.qualification || f.qual || f.occupation || f.occ))
      .map((f) => [
        String(f.relationship || f.relation || "-"),
        String(f.nomineeName || f.name || "-"),
        f.age !== null && f.age !== undefined && f.age !== "" ? String(f.age) : "-",
        String(f.qualification || f.qual || "-"),
        String(f.occupation || f.occ || "-"),
      ]);
  } else {
    const legacyMap = [
      { rel: "Father", name: empData.fatherName, age: empData.fatherAge, qual: empData.fatherQual, occ: empData.fatherOcc },
      { rel: "Mother", name: empData.motherName, age: empData.motherAge, qual: empData.motherQual, occ: empData.motherOcc },
      { rel: "Children's", name: empData.childrenName, age: empData.childrenAge, qual: empData.childrenQual, occ: empData.childrenOcc },
      { rel: "Spouse", name: empData.spouseName, age: empData.spouseAge, qual: empData.spouseQual, occ: empData.spouseOcc },
      { rel: "Brothers", name: empData.brotherName, age: empData.brotherAge, qual: empData.brotherQual, occ: empData.brotherOcc },
      { rel: "Sisters", name: empData.sisterName, age: empData.sisterAge, qual: empData.sisterQual, occ: empData.sisterOcc },
    ];

    familyRows = legacyMap
      .filter((item) => item.name || item.age || item.qual || item.occ)
      .map((item) => [
        item.rel,
        item.name || "",
        item.age ? String(item.age) : "",
        item.qual || "",
        item.occ || "",
      ]);
  }

  autoTable(doc, {
    startY: layout.y,
    head: [["Relationship", "Name", "Age", "Qualification", "* Occupation"]],
    body: familyRows,
    styles: getBaseStyles(),
    headStyles: getHeaderStyles(),
    margin: { left: config.margin, right: config.margin },
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 90, fontStyle: customFontFamily.bold },
      1: { cellWidth: 160 },
      2: { cellWidth: 50, halign: "center" },
      3: { cellWidth: 110 },
      4: { cellWidth: 125 },
    },
  });

  layout.afterTable(14);

  doc.setFont(customFontFamily.italicFont, customFontFamily.italic);
  doc.setFontSize(8.5);
  doc.text("*Occupation – In case of service please specify the organization. In case of business specify nature of business.", startX, layout.y);
  layout.advance(30);

  // Card details at bottom of Page 1
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text("PAN Card No: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.panId || "", startX + 70, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Aadhar Card No: ", startX + 230, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.aadharId || "", startX + 315, layout.y);
  layout.advance(18);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Passport No. & Valid up to: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.passportId || "", startX + 130, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Blood Group: ", startX + 300, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.bloodGroup || "", startX + 365, layout.y);
  layout.advance(18);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("UAN No: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.pfNumber || "", startX + 50, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("PF No: ", startX + 200, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.pfNumber || "", startX + 240, layout.y);

  // -------------------------------------------------------------
  // PAGE 2 - QUALIFICATION, SKILLS & LANGUAGES
  // -------------------------------------------------------------
  doc.addPage();
  layout.y = config.contentTop + 10;

  // Header: Qualification:
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11.5);
  doc.text("Qualification:", startX, layout.y);
  layout.advance(12);

  const allQualCandidates = [
    { title: empData.GraduationName ? "BE / Graduation" : "Graduation", loc: empData.GraduationLocation, inst: empData.GraduationName, yrFrom: "", yrTo: empData.GraduationPassyear, pct: empData.GraduationPercentage },
    { title: empData.HSCName ? "HSC / Diploma" : "HSC", loc: empData.HSCLocation, inst: empData.HSCName, yrFrom: "", yrTo: empData.HSCPassyear, pct: empData.HSCPercentage },
    { title: empData.SSCName ? "SSC" : "SSC", loc: empData.SSCLocation, inst: empData.SSCName, yrFrom: "", yrTo: empData.SSCPassyear, pct: empData.SSCPersentage || empData.SSCPercentage },
  ];

  const qualList = Array.isArray(empData.educationList) && empData.educationList.length > 0
    ? empData.educationList
    : (Array.isArray(empData.qualifications) && empData.qualifications.length > 0 ? empData.qualifications : []);

  let qualRows = [];
  if (qualList.length > 0) {
    qualRows = qualList
      .filter((q) => q && (q.title || q.qualification || q.location || q.institution || q.institute || q.passYear || q.yearTo || q.toYear || q.percentage))
      .map((q) => [
        String(q.title || q.qualification || ""),
        String(q.location || ""),
        String(q.institution || q.institute || ""),
        String(q.fromYear || q.yearFrom || ""),
        String(q.passYear || q.yearTo || q.toYear || ""),
        q.percentage ? `${q.percentage}%` : ""
      ]);
  } else {
    qualRows = allQualCandidates
      .filter((q) => q.inst || q.loc || q.yrTo || q.pct)
      .map((q) => [
        q.title,
        q.loc || "",
        q.inst || "",
        q.yrFrom || "",
        q.yrTo || "",
        q.pct ? `${q.pct}%` : ""
      ]);
  }

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "Qualification Title", rowSpan: 2 },
        { content: "Location", rowSpan: 2 },
        { content: "Institution/training provider/\nBoard/University", rowSpan: 2 },
        { content: "Year Attended", colSpan: 2, halign: "center" },
        { content: "% Marks/ Grade", rowSpan: 2 }
      ],
      [
        { content: "From", halign: "center" },
        { content: "To", halign: "center" }
      ]
    ],
    body: qualRows,
    styles: getBaseStyles(),
    headStyles: getHeaderStyles(),
    margin: { left: config.margin, right: config.margin },
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 105, fontStyle: customFontFamily.bold },
      1: { cellWidth: 70 },
      2: { cellWidth: 170 },
      3: { cellWidth: 45, halign: "center" },
      4: { cellWidth: 45, halign: "center" },
      5: { cellWidth: 100, halign: "center" },
    },
  });

  layout.afterTable(30);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text("Computer Literacy: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.compLiteracy || empData.computerLiteracy || "", startX + 95, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Explain breaks (If any): ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.explainBreak || empData.explainBreaks || "", startX + 115, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Certification / Courses (If any): ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.certiCourses || empData.certifications || "", startX + 150, layout.y);
  layout.advance(17.5);

  const uStudy = String(empData.undertakingStudy || "").trim();
  const isUStudyYes = uStudy.startsWith("Yes");
  const isUStudyNo = !isUStudyYes;
  const isFull = uStudy.includes("Full time");
  const isPart = uStudy.includes("Part time");
  const isDist = uStudy.includes("Distance");
  const isOth = uStudy.includes("Other");

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Are you currently undertaking study/training? (tick one)", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  drawCheckbox(doc, startX + 280, layout.y + 1, isUStudyYes, "Yes");
  drawCheckbox(doc, startX + 340, layout.y + 1, isUStudyNo, "No");
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("If yes, course/program name: (tick one)", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  drawCheckbox(doc, startX + 220, layout.y + 1, isFull, "Full time");
  drawCheckbox(doc, startX + 280, layout.y + 1, isPart, "Part time");
  drawCheckbox(doc, startX + 340, layout.y + 1, isDist, "Distance");
  drawCheckbox(doc, startX + 400, layout.y + 1, isOth, "Other");
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Subject of special study OR Researcher work: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.specialStudy || empData.researchWork || "", startX + 215, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Special Skills: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.SpecialSkill || empData.specialSkills || "", startX + 75, layout.y);
  layout.advance(30);

  // Section Header: Language Proficiency:
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11.5);
  doc.text("Language Proficiency:", startX, layout.y);
  layout.advance(12);

  const getLangProf = (langName) => {
    let rawStr = "";
    if (langName.toLowerCase() === "english") rawStr = empData.engLang;
    else if (langName.toLowerCase() === "hindi") rawStr = empData.hindiLang;
    else if (langName.toLowerCase() === "marathi") rawStr = empData.marathiLang;

    if (rawStr) {
      const parts = String(rawStr).split(",");
      return {
        speak: parts[0] || "Fluent",
        read: parts[1] || "Fluent",
        write: parts[2] || "Fluent",
      };
    }

    let list = [];
    if (Array.isArray(empData.languageProficiency)) {
      list = empData.languageProficiency;
    } else if (typeof empData.languageProficiency === "string") {
      try { list = JSON.parse(empData.languageProficiency); } catch (_) {}
    }
    const item = Array.isArray(list)
      ? list.find((l) => l && typeof l === "object" && l.language && String(l.language).toLowerCase() === String(langName).toLowerCase())
      : null;
    return item || { speak: "Fluent", read: "Fluent", write: "Fluent" };
  };

  const buildLangRow = (langName) => {
    const prof = getLangProf(langName);
    return [
      langName,
      prof.speak === "Fluent" ? "Yes" : "",
      prof.speak === "Fair" ? "Yes" : "",
      prof.speak === "Poor" ? "Yes" : "",
      prof.read === "Fluent" ? "Yes" : "",
      prof.read === "Fair" ? "Yes" : "",
      prof.read === "Poor" ? "Yes" : "",
      prof.write === "Fluent" ? "Yes" : "",
      prof.write === "Fair" ? "Yes" : "",
      prof.write === "Poor" ? "Yes" : "",
    ];
  };

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "Language", rowSpan: 2 },
        { content: "Speak", colSpan: 3, halign: "center" },
        { content: "Read", colSpan: 3, halign: "center" },
        { content: "Write", colSpan: 3, halign: "center" }
      ],
      [
        "Fluent", "Fair", "Poor",
        "Fluent", "Fair", "Poor",
        "Fluent", "Fair", "Poor"
      ]
    ],
    body: [
      buildLangRow("English"),
      buildLangRow("Hindi"),
      buildLangRow("Marathi"),
    ],
    styles: getBaseStyles(),
    headStyles: getHeaderStyles(),
    margin: { left: config.margin, right: config.margin },
    theme: "grid",
  });

  layout.afterTable(30);

  // Previous employment (Most recent first) at bottom of Page 2
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11.5);
  doc.text("Previous employment (Most recent first)", startX, layout.y);
  layout.advance(12);

  const expList = Array.isArray(empData.experienceList) && empData.experienceList.length > 0
    ? empData.experienceList
    : (Array.isArray(empData.experiences) && empData.experiences.length > 0 ? empData.experiences : []);

  let prevExpRows = expList.length > 0
    ? expList
        .filter((e) => e && (e.companyName || e.fromDate || e.toDate || e.designation || e.leavingReason || e.reasonForLeaving || e.location))
        .map((e) => [
          String(e.companyName || ""),
          (e.fromDate || e.toDate) ? `${e.fromDate ? formatDateStr(e.fromDate) : ""} to ${e.toDate ? formatDateStr(e.toDate) : ""}` : "",
          String(e.designation || ""),
          String(e.leavingReason || e.reasonForLeaving || ""),
          "",
          String(e.location || "")
        ])
    : [];

  autoTable(doc, {
    startY: layout.y,
    head: [["Employer name", "Dates from/to", "Position held", "Reason for leaving", "Office use check initial/date", "Location"]],
    body: prevExpRows,
    styles: getBaseStyles(),
    headStyles: getHeaderStyles(),
    margin: { left: config.margin, right: config.margin },
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 100, fontStyle: customFontFamily.bold },
      1: { cellWidth: 90 },
      2: { cellWidth: 90 },
      3: { cellWidth: 105 },
      4: { cellWidth: 95 },
      5: { cellWidth: 55 },
    },
  });

  layout.afterTable(14);

  const latestExp = expList.length > 0 ? expList[0] : {};
  const lastSal = latestExp.lastSalary || empData.lastSalary || "";
  const inHand = latestExp.inHandCTC || empData.inHandSalary || "";
  const hrContact = latestExp.hrNameContact || empData.hrContactDetails || "";

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text("Last Salary: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(lastSal, startX + 65, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("In Hand PM / ", startX + 160, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(inHand, startX + 225, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("CTC Per Annum", startX + 360, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("HR Name & Contact Details: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(hrContact, startX + 135, layout.y);
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("(From Last Company)", startX + 330, layout.y);

  // -------------------------------------------------------------
  // PAGE 3 - REFEREES & OTHER DETAILS
  // -------------------------------------------------------------
  doc.addPage();
  layout.y = config.contentTop + 10;

  const agreeContact = empData.agreeRefereesContact === "Yes";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Do you agree to have referees contacted in relation to this application? (tick one)", startX, layout.y);
  drawCheckbox(doc, startX + 400, layout.y - 1, agreeContact, "Yes");
  drawCheckbox(doc, startX + 460, layout.y - 1, !agreeContact, "No");
  layout.advance(17.5);

  doc.setFont(customFontFamily.italicFont, customFontFamily.italic);
  doc.setFontSize(8.5);
  doc.text("(Reference checks will be conducted legally in an ethical manner and all information derived will remain confidential.)", startX, layout.y);
  layout.advance(30);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.setFontSize(9.5);
  doc.text("Please provide details of three people who can speak on your behalf regarding your work history.", startX, layout.y);
  layout.advance(12);

  const refereesList = Array.isArray(empData.referees) && empData.referees.length > 0
    ? empData.referees
    : (Array.isArray(empData.refereesData) && empData.refereesData.length > 0 ? empData.refereesData : []);

  let refereeRows = (refereesList.length > 0)
    ? refereesList
        .filter((r) => r && (r.name || r.Name || r.refereeName || r.contactNo || r.contactNumber || r.position || r.positionHeld))
        .map((r) => [
          String(r.name || r.Name || r.refereeName || ""),
          String(r.contactNo || r.contactNumber || ""),
          String(r.position || r.positionHeld || ""),
          ""
        ])
    : [];

  autoTable(doc, {
    startY: layout.y,
    head: [["Name", "Contact No.", "Position held/working relationship (e.g. supervisor)", "Office use check initial/date"]],
    body: refereeRows,
    styles: getBaseStyles(),
    headStyles: getHeaderStyles(),
    margin: { left: config.margin, right: config.margin },
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 120, fontStyle: customFontFamily.bold },
      1: { cellWidth: 100 },
      2: { cellWidth: 190 },
      3: { cellWidth: 125 },
    },
  });

  layout.afterTable(30);

  const workAvail = empData.workTypeAvailable || "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text("What type of work are you available for? (tick one)", startX, layout.y);
  drawCheckbox(doc, startX + 240, layout.y, workAvail === "Full time", "Full time");
  drawCheckbox(doc, startX + 300, layout.y, workAvail === "Part Time", "Part Time");
  drawCheckbox(doc, startX + 360, layout.y, workAvail === "On Call Basis", "On Call Basis");
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Notice to be serve current company or Joining Date:", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.noticePeriodOrJoiningDate || empData.noticePeriod || "", startX + 240, layout.y);
  layout.advance(17.5);

  const drawYesNoQuestion = (question, isYes = false) => {
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(9);
    doc.text(question, startX, layout.y);
    drawCheckbox(doc, startX + 250, layout.y, isYes, "Yes");
    drawCheckbox(doc, startX + 300, layout.y, !isYes, "No");
    layout.advance(17.5);
  };

  drawYesNoQuestion("Have you ever been terminated by your past employer ?: ", empData.terminatedPastEmployer === "Yes");
  
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9);
  doc.text("Are you connected with any business ?: ", startX, layout.y);
  drawCheckbox(doc, startX + 185, layout.y, empData.connectedWithBusiness === "Yes", "Yes");
  drawCheckbox(doc, startX + 225, layout.y, empData.connectedWithBusiness !== "Yes", "No");
  doc.text("?", startX + 475, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Are you in position to make your own house arrangement ?: ", startX, layout.y);
  drawCheckbox(doc, startX + 270, layout.y, empData.ownHouseArrangement === "Yes", "Yes");
  drawCheckbox(doc, startX + 310, layout.y, empData.ownHouseArrangement !== "Yes", "No");
  doc.text("?", startX + 475, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Are you prepared to service any where in India & Abroad ?: ", startX, layout.y);
  drawCheckbox(doc, startX + 265, layout.y, empData.serveIndiaAbroad === "Yes", "Yes");
  drawCheckbox(doc, startX + 305, layout.y, empData.serveIndiaAbroad !== "Yes", "No");
  doc.text("?", startX + 475, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Do you have any friends or relatives in DYC Global ?: ", startX, layout.y);
  drawCheckbox(doc, startX + 240, layout.y, empData.friendsRelativesInDYC === "Yes", "Yes");
  drawCheckbox(doc, startX + 280, layout.y, empData.friendsRelativesInDYC !== "Yes", "No");
  doc.text("?", startX + 475, layout.y);
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Who referred you DYC ? (Specify Name & Relation):", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.whoReferredDYC || empData.referredBy || "", startX + 235, layout.y);
  layout.advance(17.5);

  const isInterviewed = empData.interviewedBeforeDYC === "Yes";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Have you been interviewed before by DYC Global ?: ", startX, layout.y);
  drawCheckbox(doc, startX + 235, layout.y, isInterviewed, "Yes");
  drawCheckbox(doc, startX + 275, layout.y, !isInterviewed, `No / If Yes pls specify Date (${empData.interviewedBeforeDYCDate || "   /   /   "})`);
  layout.advance(30);

  const isGovtRel = empData.relativeInGovtOrg === "Yes";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text("Do you have any relative working in Govt. Organization?", startX, layout.y);
  drawCheckbox(doc, startX + 265, layout.y, isGovtRel, "Yes");
  drawCheckbox(doc, startX + 305, layout.y, !isGovtRel, "No");
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("If Yes pls specify Name of relative and working Dept.:", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.relativeInGovtOrgDetails || "", startX + 235, layout.y);
  layout.advance(17.5);

  const isBizRel = empData.relativeOwnBusiness === "Yes";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Do you have any relatives who own a company or business?", startX, layout.y);
  drawCheckbox(doc, startX + 265, layout.y, isBizRel, "Yes");
  drawCheckbox(doc, startX + 305, layout.y, !isBizRel, "No");
  layout.advance(17.5);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("If Yes pls specify Name of relative and Company Name & Nature of Business.:", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.relativeOwnBusinessDetails || "", startX + 335, layout.y);
  layout.advance(18);

  // Header: Conflict of Interest Disclosure and Background Check Consent
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11.5);
  doc.text("Conflict of Interest Disclosure and Background Check Consent:", startX, layout.y);
  layout.advance(22);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.setFontSize(9.5);
  doc.text("I ", startX, layout.y);
  doc.text(empData.name || "", startX + 20, layout.y);
  doc.text(", understand the importance of maintaining impartiality and transparency in ", startX + 150, layout.y);
  layout.advance(16);
  doc.text("inspection / Office activities. I declare the following:", startX, layout.y);
  layout.advance(30);
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11);
  doc.text("Conflict of Interest Disclosure:", startX, layout.y);
  layout.advance(16);

  const para1 = "I confirm that I do not have any known personal, financial, or professional relationships with current or potential clients, suppliers, or competitors of DYC Global Pvt. Ltd., that could influence my ability to perform my duties impartially.";
  const lines1 = doc.splitTextToSize(para1, contentW);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  // doc.setFontSize(9.5);
  doc.text(lines1, startX, layout.y);
  layout.advance(lines1.length * 15 + 18);

  const para2 = "If applicable, I disclose below any prior associations, relationships, or circumstances that may be considered a conflict of interest:";
  const lines2 = doc.splitTextToSize(para2, contentW);
  doc.text(lines2, startX, layout.y);
  layout.advance(lines2.length * 15 + 18);

  // -------------------------------------------------------------
  // PAGE 4 - CONFLICT OF INTEREST DISCLOSURE & CONSENT
  // -------------------------------------------------------------
  doc.addPage();
  layout.y = config.contentTop + 10;
  layout.advance(24);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Details of Potential Conflict of Interest (if any):", startX, layout.y);
  layout.advance(22);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text("(Write \"None\" if no potential COI exists.)", startX, layout.y);
  layout.advance(17.5);

  doc.text(empData.potentialConflictOfInterest || "None", startX, layout.y);
  layout.advance(32);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11);
  doc.text("Background Check Consent:", startX, layout.y);
  layout.advance(17.5);

  const para3 = "I consent to DYC Global Pvt. Ltd. conducting background verification, including employment history, professional references, and other relevant checks, to assess my suitability for the role.";
  const lines3 = doc.splitTextToSize(para3, contentW);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.setFontSize(9.5);
  doc.text(lines3, startX, layout.y);
  layout.advance(lines3.length * 10 + 10);

  const para4 = "I agree to provide any additional information or documentation required to facilitate this process.";
  const lines4 = doc.splitTextToSize(para4, contentW);
  doc.text(lines4, startX, layout.y);
  layout.advance(lines4.length * 10 + 10);

  const para5 = "I certify that the information provided in this application is accurate and complete to the best of my knowledge.";
  const lines5 = doc.splitTextToSize(para5, contentW);
  doc.text(lines5, startX, layout.y);
  layout.advance(lines5.length * 10 + 10);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Signature: ", startX, layout.y);
  layout.advance(36);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Date: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(formatDateStr(empData.dateOfJoining || new Date()), startX + 35, layout.y);

  layout.advance(30);



  // DECLARATION SECTION
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(11);
  doc.text("Declaration", startX, layout.y);
  layout.advance(17.5);

  const decPara1 = "I hereby declare that the information provided in this application is true, accurate, and complete to the best of my knowledge.";
  const decLines1 = doc.splitTextToSize(decPara1, contentW);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.setFontSize(9);
  doc.text(decLines1, startX, layout.y);
  layout.advance(decLines1.length * 11 + 8);

  const decPara2 = "I understand that any false, misleading, or withheld information may result in immediate termination of employment. This application does not guarantee an offer of employment.";
  const decLines2 = doc.splitTextToSize(decPara2, contentW);
  doc.text(decLines2, startX, layout.y);
  layout.advance(decLines2.length * 11 + 8);

  const decPara3 = "DYC Global Pvt. Ltd. reserves the right to conduct background verification and checks as required, and I shall be notified accordingly.";
  const decLines3 = doc.splitTextToSize(decPara3, contentW);
  doc.text(decLines3, startX, layout.y);
  layout.advance(decLines3.length * 11 + 12);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Signature: ", startX, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Date: ", startX + 270, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(formatDateStr(empData.dateOfJoining || new Date()), startX + 305, layout.y);
  layout.advance(32);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9);
  doc.text("Name: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.name || "", startX + 45, layout.y);
  layout.advance(30);



  // Header: EMPLOYER SECTION
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(12.5);
  doc.text("EMPLOYER SECTION", startX, layout.y);
  layout.advance(20);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Confidential – Reference Checks ", startX, layout.y);
  doc.setFont(customFontFamily.italicFont, customFontFamily.italic);
  doc.text("for Office Use Only", startX + 160, layout.y);
  layout.advance(17.5);

  const interviewedByText = empData.interviewedBy || "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Interviewed by: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(interviewedByText, startX + 85, layout.y);
  layout.advance(17.5);

  const firstIntDate = empData.firstInterviewDate ? formatDateStr(empData.firstInterviewDate) : "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("First Interview Date: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(firstIntDate, startX + 105, layout.y);

  const secondIntDate = empData.secondInterviewDate ? formatDateStr(empData.secondInterviewDate) : "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Second Interview Date: ", startX + 240, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(secondIntDate, startX + 355, layout.y);
  layout.advance(17.5);

  const hrRemarksText = empData.hrRemarks || empData.remarks || "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Remarks: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(hrRemarksText, startX + 55, layout.y);
  layout.advance(17.5);

  const neatnessText = empData.neatness || "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Neatness: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(neatnessText, startX + 60, layout.y);

  const abilityText = empData.ability || "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Ability: ", startX + 200, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(abilityText, startX + 285, layout.y);
  layout.advance(17.5);

  const isHiredBool = empData.isHired === true || empData.isHired === "Yes" || Boolean(empData.employeeStatus);
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Hired: ", startX, layout.y);
  drawCheckbox(doc, startX + 40, layout.y, isHiredBool, "Yes");
  drawCheckbox(doc, startX + 85, layout.y, !isHiredBool, "No");

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Position: ", startX + 200, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.hrDesignation || empData.designation || "", startX + 240, layout.y);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text("Dept.: ", startX + 380, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(empData.department || "", startX + 410, layout.y);
  layout.advance(17.5);

  const salText = (empData.salaryWage && (String(empData.salaryWage).toLowerCase() === "salary" || String(empData.salaryWage).toLowerCase() === "wage"))
    ? empData.salaryWage
    : ((String(empData.employeeType || empData.employmentType || "").toLowerCase().includes("wage") || String(empData.employeeType || empData.employmentType || "").toLowerCase().includes("daily") || String(empData.employeeType || empData.employmentType || "").toLowerCase().includes("contract")) ? "Wage" : "Salary");
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Salary/Wage: ", startX, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(salText, startX + 75, layout.y);

  const repDate = empData.dateReportingToWork || empData.actualJoiningDate || empData.confirmedJoiningDate || empData.dateOfJoining;
  const repDateText = repDate ? formatDateStr(repDate) : "";
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Date Reporting To Work: ", startX + 200, layout.y);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(repDateText, startX + 320, layout.y);
  layout.advance(30);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("Approved: 1. ", startX, layout.y);
  doc.text("2. ", startX + 180, layout.y);
  doc.text("3. ", startX + 310, layout.y);
  layout.advance(12);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10);
  doc.text("(HR MANAGER)", startX + 65, layout.y);
  doc.text("(DEPT. HEAD)", startX + 195, layout.y);
  doc.text("(TECHNICAL MANAGER / MD – TECHNICAL)", startX + 320, layout.y);

  // Apply 2-pass header/footer/border decorator across all 5 pages
  const totalPages = doc.internal.getNumberOfPages();
  addPageDecorators(doc, totalPages, empData);

  return doc;
};
